<template>
  <a-modal
    title="Create Associate"
    :visible="visible"
    :destroy-on-close="true"
    :width="1200"
    data-testid="associate-profiles-create-modal"
    @cancel="closeModal"
  >
    <a-form-model
      layout="horizontal"
      ref="form"
      class="flex gap-6"
      :model="form"
      :rules="rules"
      :label-col="{ span: 24 }"
      :wrapper-col="{ span: 24 }"
      data-testid="associate-profiles-create-form"
    >
      <div class="wrapper">
        <div class="flex flex-column gap-2" data-testid="associate-profiles-create-personal-section">
          <h3>Personal Info</h3>
          <a-form-model-item
            :colon="false"
            label-align="left"
            label="First Name"
            prop="user_info.first_name"
            :validate-status="invalidChar.first_name ? 'error' : ''"
            :help="getHelpText('first_name')"
          >
            <a-input
              v-model="form.user_info.first_name"
              :disabled="isCreatingAssociate"
              placeholder="Enter first name"
              autocomplete="off"
              data-testid="associate-profiles-create-first-name-input"
              @input="handleNameInput('first_name', $event)"
            />
          </a-form-model-item>

          <a-form-model-item
            :colon="false"
            label-align="left"
            label="Last Name"
            prop="user_info.last_name"
            :validate-status="invalidChar.last_name ? 'error' : ''"
            :help="getHelpText('last_name')"
          >
            <a-input
              v-model="form.user_info.last_name"
              :disabled="isCreatingAssociate"
              placeholder="Enter last name"
              autocomplete="off"
              data-testid="associate-profiles-create-last-name-input"
              @input="handleNameInput('last_name', $event)"
            />
          </a-form-model-item>

          <a-form-model-item :colon="false" label-align="left" label="Email" prop="user_info.email">
            <a-input
              v-model="form.user_info.email"
              :disabled="isCreatingAssociate"
              placeholder="Enter email"
              autocomplete="off"
              data-testid="associate-profiles-create-email-input"
            />
          </a-form-model-item>
          <a-form-model-item :colon="false" label-align="left" label="Phone" prop="user_info.phone">
            <phone-input
              @input="
                value => {
                  $refs.form.validateField('user_info.phone')
                  form.user_info.phone = value
                }
              "
              :value="form.user_info.phone"
              data-testid="associate-profiles-create-phone-input"
            />
          </a-form-model-item>

          <a-form-model-item :colon="false" label-align="left" label="Birth Date" prop="user_info.birth_date">
            <a-date-picker
              v-model="form.user_info.birth_date"
              placeholder="Birth Date"
              class="w-full"
              :allowClear="true"
              :format="visualDateFormat"
              data-testid="associate-profiles-create-birth-date-input"
            />
          </a-form-model-item>

          <a-form-model-item
            :colon="false"
            label-align="left"
            label="Associate Number"
            prop="associate_info.associate_number"
          >
            <a-input
              v-model="form.associate_info.associate_number"
              placeholder="Enter number"
              autocomplete="off"
              data-testid="associate-profiles-create-associate-number-input"
            />
          </a-form-model-item>

          <a-form-model-item
            :colon="false"
            label-align="left"
            :label="getTaxIdentifierLabel(form.associate_info.is_ssn)"
            prop="associate_info.social_security_number"
          >
            <tax-identifier-input
              v-model="form.associate_info.social_security_number"
              :is-ssn.sync="form.associate_info.is_ssn"
              :disabled="isCreatingAssociate"
              :input-attrs="{ 'data-testid': 'associate-profiles-create-ssn-input' }"
              @type-change="revalidateTaxIdentifier('associate_info.social_security_number')"
            />
          </a-form-model-item>
        </div>
        <div class="flex flex-column gap-2" data-testid="associate-profiles-create-address-section">
          <h3>Address</h3>
          <a-form-model-item
            :colon="false"
            label-align="left"
            label="Address Line 1"
            prop="user_info.address.address_line1"
          >
            <a-input
              v-model="form.user_info.address.address_line1"
              :disabled="isCreatingAssociate"
              placeholder="Enter Address"
              data-testid="associate-profiles-create-address-line1-input"
            />
          </a-form-model-item>
          <a-form-model-item
            :colon="false"
            label-align="left"
            label="Address Line 2"
            prop="user_info.address.address_line2"
          >
            <a-input
              v-model="form.user_info.address.address_line2"
              :disabled="isCreatingAssociate"
              placeholder="Enter Address"
              data-testid="associate-profiles-create-address-line2-input"
            />
          </a-form-model-item>

          <a-form-model-item :colon="false" label-align="left" label="City" prop="user_info.address.city">
            <a-input
              v-model="form.user_info.address.city"
              :disabled="isCreatingAssociate"
              placeholder="Enter City"
              data-testid="associate-profiles-create-city-input"
            />
          </a-form-model-item>

          <a-form-model-item :colon="false" label-align="left" label="Country" prop="user_info.address.country">
            <a-select
              v-model="form.user_info.address.country"
              :disabled="isCreatingAssociate"
              placeholder="Enter Country"
              data-testid="associate-profiles-create-country-select"
            >
              <a-select-option v-for="country in countriesList" :key="country.code" :value="country.code">
                {{ country.name }}
              </a-select-option>
            </a-select>
          </a-form-model-item>

          <a-form-model-item
            v-if="form.user_info.address.country === 'US'"
            :colon="false"
            label-align="left"
            label="State"
            prop="user_info.address.state"
          >
            <a-select
              v-model="form.user_info.address.state"
              :disabled="isCreatingAssociate"
              placeholder="Enter State"
              data-testid="associate-profiles-create-state-select"
            >
              <a-select-option v-for="state in statesList" :key="state.code" :value="state.code">
                {{ state.name }}
              </a-select-option>
            </a-select>
          </a-form-model-item>

          <a-form-model-item
            :colon="false"
            label-align="left"
            label="Zipcode"
            prop="user_info.address.zipcode"
          >
            <a-input
              v-model="form.user_info.address.zipcode"
              :disabled="isCreatingAssociate"
              placeholder="Enter Zipcode"
              data-testid="associate-profiles-create-zipcode-input"
            />
          </a-form-model-item>
        </div>
        <div class="flex flex-column gap-2" data-testid="associate-profiles-create-project-section">
          <h3>Project</h3>

          <a-form-model-item :colon="false" label-align="left" label="Position" prop="associate_info.role">
            <custom-select
              :multiple="false"
              :allow-clear="false"
              :value.sync="form.associate_info.role"
              placeholder="Position"
              :options="roles"
              name="name"
              optionKey="id"
              data-testid="associate-profiles-create-position-select"
            />
          </a-form-model-item>

          <a-form-model-item
            :colon="false"
            label-align="left"
            label="Schedule Type"
            prop="associate_info.schedule_type"
          >
            <a-select
              v-model="form.associate_info.schedule_type"
              placeholder="Enter schedule type"
              :allowClear="true"
              data-testid="associate-profiles-create-schedule-type-select"
            >
              <a-select-option v-for="(value, key) in scheduleTypes" :key="key" :value="key">
                {{ value }}
              </a-select-option>
            </a-select>
          </a-form-model-item>
          <a-form-model-item :colon="false" label-align="left" label="PBJ ID" prop="associate_info.pbj_id">
            <a-input
              v-model="form.associate_info.pbj_id"
              placeholder="PBJ ID"
              autocomplete="off"
              data-testid="associate-profiles-create-pbj-id-input"
            />
          </a-form-model-item>

          <a-form-model-item :colon="false" label-align="left" label="Payroll ID" prop="associate_info.payroll_id">
            <a-input
              v-model="form.associate_info.payroll_id"
              placeholder="Payroll ID"
              autocomplete="off"
              data-testid="associate-profiles-create-payroll-id-input"
            />
          </a-form-model-item>

          <h3>Payment</h3>
          <a-form-model-item :colon="false" label-align="left" label="Payment Type" prop="payment_info.type">
            <a-select
              v-model="form.payment_info.type"
              :disabled="is_create_legal_entity"
              placeholder="Enter payment type"
              data-testid="associate-profiles-create-payment-type-select"
            >
              <a-select-option v-for="(value, key) in paymentTypes" :key="key" :value="key">
                {{ value }}
              </a-select-option>
            </a-select>
          </a-form-model-item>
          <a-form-model-item
            v-if="form.payment_info.type !== 'volunteer'"
            :colon="false"
            label-align="left"
            label="Payment Method"
            prop="payment_info.method"
          >
            <a-select
              v-model="form.payment_info.method"
              placeholder="Enter payment method"
              data-testid="associate-profiles-create-payment-method-select"
            >
              <a-select-option
                v-for="(value, key) in paymentMethods"
                :key="key"
                :value="key"
                :disabled="isDisabledPaymentMethod && key === 'marketplace_rate'"
              >
                {{ value }}
              </a-select-option>
            </a-select>
          </a-form-model-item>
          <a-form-model-item
            v-if="form.payment_info.type === 'contractor'"
            :colon="false"
            label-align="left"
            label="Legal Entity"
            prop="payment_info.legal_entity"
          >
            <custom-select
              :multiple="false"
              :disabled="is_create_legal_entity"
              :allow-clear="false"
              :value.sync="form.payment_info.legal_entity"
              placeholder="Legal Entity"
              :options="agencies"
              name="name"
              optionKey="id"
              data-testid="associate-profiles-create-legal-entity-select"
            >
              <template #option="{ option }"> [{{ option.id }}] - {{ option.name }}</template>
            </custom-select>

            <a-button
              @click="handleOpenLegalEntity"
              type="primary"
              class="w-full"
              data-testid="associate-profiles-create-legal-entity-button"
              >Create New</a-button
            >
          </a-form-model-item>

          <a-form-model-item
            v-if="form.payment_info.method === 'hourly'"
            :colon="false"
            label-align="left"
            label="Payment Status"
            prop="payment_info.status"
          >
            <a-select
              v-model="form.payment_info.status"
              placeholder="Enter payment status"
              :allowClear="true"
              data-testid="associate-profiles-create-payment-status-select"
            >
              <a-select-option v-for="(value, key) in paymentStatuses" :key="key" :value="key">
                {{ value }}
              </a-select-option>
            </a-select>
          </a-form-model-item>

          <a-form-model-item
            v-if="isVisibleRate"
            :colon="false"
            label="Hourly Rate, $"
            label-align="left"
            prop="payment_info.hourly_rate"
          >
            <a-input-number v-model="rateProxy" :min="0" placeholder="Enter rate" data-testid="associate-profiles-create-hourly-rate-input" />
          </a-form-model-item>

          <a-form-model-item
            v-if="isVisibleRate && form.payment_info.method === 'salary'"
            :colon="false"
            label="Salary for Pay Period, $"
            label-align="left"
          >
            <a-input-number v-model="salaryProxy" :min="0" placeholder="Enter salary" data-testid="associate-profiles-create-salary-input" />
          </a-form-model-item>

          <a-form-model-item
            :colon="false"
            label="Orientation Rate, $"
            label-align="left"
            prop="payment_info.orientation_rate"
          >
            <a-input-number
              v-model="orientationRateProxy"
              :min="0"
              placeholder="Enter Orientation Rate"
              data-testid="associate-profiles-create-orientation-rate-input"
            />
          </a-form-model-item>

          <a-form-model-item
            :colon="false"
            label="Training Rate, $"
            label-align="left"
            prop="payment_info.training_rate"
          >
            <a-input-number
              v-model="trainingRateProxy"
              :min="0"
              placeholder="Enter Training Rate"
              data-testid="associate-profiles-create-training-rate-input"
            />
          </a-form-model-item>
        </div>
      </div>
    </a-form-model>

    <template slot="footer">
      <div class="flex justify-content-between" data-testid="associate-profiles-create-footer">
        <a-button data-testid="associate-profiles-create-cancel-button" @click="closeModal">Cancel</a-button>
        <a-button
          data-testid="associate-profiles-create-save-button"
          @click="handleSubmit"
          icon="save"
          type="primary"
          :loading="loading"
          >Save</a-button
        >
      </div>
    </template>
  </a-modal>
</template>
<script>
import { Button, Select, Input, notification, FormModel, InputNumber, DatePicker } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import ConstData from '@/helpers/const-data'
import { deepCopy } from '@/utils/deep-copy'
import PhoneInput from '@/components/ui/phone-input.vue'
import CustomSelect from '@/components/ui/custom-select.vue'
import TaxIdentifierInput from '@/components/ui/tax-identifier-input.vue'
import { getTaxIdentifierLabel, normalizeTaxIdentifier } from '@/utils/tax-identifier'
import {
  createRequiredFieldRule,
  createRequiredTaxIdentifierRule,
  isUsCountry
} from '@/modules/profile/utils/associate-profile-completion'

const { mapActions: profileActions } = createNamespacedHelpers('profile')
const { mapActions: modalActions } = createNamespacedHelpers('modals')
const { mapActions: legalEntityActions } = createNamespacedHelpers('legalEntity')

const scheduleTypes = Object.freeze({
  fixed: 'Strict',
  self_schedule: 'Flexible',
  free: 'Free'
})

const paymentTypes = Object.freeze({
  employee: 'Employee',
  contractor: 'Contractor',
  volunteer: 'Volunteer'
})

const paymentStatuses = Object.freeze({
  on_call: 'On Call / Per Diem',
  full_time: 'Full Time',
  part_time: 'Part Time'
})

const paymentMethods = Object.freeze({
  salary: 'Salary',
  hourly: 'Hourly',
  marketplace_rate: 'Marketplace Rate'
})

const initialForm = {
  user_info: {
    first_name: undefined,
    last_name: undefined,
    email: undefined,
    phone: undefined,
    is_allow_sms: false,
    photo_id: null,
    signature_file_id: null,
    birth_date: undefined,
    address: {
      address_line1: undefined,
      address_line2: undefined,
      city: undefined,
      state: 'CA',
      country: 'US',
      zipcode: undefined
    }
  },
  associate_info: {
    associate_number: undefined,
    project_id: undefined,
    is_verified: true,
    role: undefined,
    social_security_number: undefined,
    is_ssn: true,
    schedule_type: undefined,
    pbj_id: null,
    payroll_id: null
  },
  payment_info: {
    type: undefined,
    status: undefined,
    method: undefined,
    legal_entity: null,
    hourly_rate: 0,
    training_rate: 0,
    orientation_rate: 0,
    salary: 0
  }
}

const initialRules = {
  user_info: {
    first_name: [
      {
        required: true,
        message: 'First name can not be empty',
        trigger: 'change'
      }
    ],
    last_name: [
      {
        required: true,
        message: 'Last name can not be empty',
        trigger: 'change'
      }
    ],
    email: [
      {
        required: true,
        message: 'Email can not be empty',
        trigger: 'change',
        type: 'email'
      }
    ],
    phone: [
      {
        required: true,
        message: 'Phone can not be empty',
        trigger: 'change'
      },
      {
        transform: value => (value ? value.replace(/\s+/g, '') : value),
        pattern: /^\+\d{7,18}$/,
        message: 'Invalid phone number. It should start with a + and contain 10 to 15 digits.'
      }
    ],
    birth_date: [createRequiredFieldRule('Please enter birth date.')],
    address: {
      address_line1: [createRequiredFieldRule('Please enter address line 1.')],
      city: [createRequiredFieldRule('Please enter city.')],
      country: [createRequiredFieldRule('Please enter country.')]
    }
  },
  associate_info: {
    social_security_number: [],
    role: [
      {
        required: true,
        message: 'Position can not be empty',
        trigger: 'change'
      }
    ],
    schedule_type: [
      {
        required: true,
        message: 'Schedule type can not be empty',
        trigger: 'change'
      }
    ],
    pbj_id: [
      {
        validator: (_, value) => {
          if (!value || /^\d+$/.test(value)) {
            return Promise.resolve()
          }
          return Promise.reject(new Error('Only digits are allowed.'))
        },
        trigger: 'change'
      }
    ],
    payroll_id: [
      {
        validator: (_, value) => {
          if (!value || /^\d+$/.test(value)) {
            return Promise.resolve()
          }
          return Promise.reject(new Error('Only digits are allowed.'))
        },
        trigger: 'change'
      }
    ]
  },
  payment_info: {
    type: [
      {
        required: true,
        message: 'Payment type can not be empty',
        trigger: 'change'
      }
    ],
    method: [
      {
        required: true,
        message: 'Payment method can not be empty',
        trigger: 'change'
      }
    ]
  }
}

export default {
  name: 'create-associate-modal',
  components: {
    'a-button': Button,
    'a-input': Input,
    'a-input-number': InputNumber,
    'a-select': Select,
    'a-select-option': Select.Option,
    'a-form-model-item': FormModel.Item,
    'a-form-model': FormModel,
    'a-date-picker': DatePicker,
    'phone-input': PhoneInput,
    'custom-select': CustomSelect,
    'tax-identifier-input': TaxIdentifierInput
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    user_id: {
      type: Number,
      default: undefined
    },
    is_create_legal_entity: {
      type: Boolean,
      default: false
    },
    creation_mode: {
      type: String,
      default: undefined
    }
  },
  emits: ['add'],
  data() {
    return {
      loading: false,

      form: { ...initialForm },
      rules: {
        ...initialRules,
        user_info: {
          ...initialRules.user_info,
          address: {
            ...initialRules.user_info.address
          },
          first_name: [
            {
              required: true,
              message: 'Please enter your first name',
              trigger: 'blur'
            },
            { validator: this.associateNameValidator, trigger: 'blur' }
          ],
          last_name: [
            {
              required: true,
              message: 'Please enter your last name',
              trigger: 'blur'
            },
            { validator: this.associateNameValidator, trigger: 'blur' }
          ]
        },
        associate_info: {
          ...initialRules.associate_info,
          associate_number: [
            {
              required: true,
              message: 'Associate number can not be empty',
              trigger: 'change'
            },
            {
              len: 6,
              message: 'The associate number must be 6 characters.',
              trigger: 'change'
            },
            {
              validator: this.associateNumberValidator,
              trigger: 'change'
            }
          ],
          social_security_number: [
            createRequiredTaxIdentifierRule(() => this.form.associate_info.is_ssn)
          ]
        }
      },

      countriesList: ConstData.countries,
      statesList: ConstData.usStates,
      paymentMethods: paymentMethods,
      paymentStatuses: paymentStatuses,
      paymentTypes: paymentTypes,
      scheduleTypes: scheduleTypes,
      roles: this.$store.state.applicationState.projectRoles,

      baseHelpText: 'Only letters, spaces, hyphens (-), and slashes (/) allowed.',
      invalidChar: {
        first_name: null,
        last_name: null
      }
    }
  },
  watch: {
    visible: {
      handler(newVal) {
        if (!newVal) {
          this.form = deepCopy(initialForm)
          this.syncUsAddressRules(false)
          return
        }

        this.syncUsAddressRules()
      },
      immediate: true
    },
    'form.user_info.address.country': {
      handler(newVal) {
        const isUs = newVal === 'US'

        if (!isUs) this.form.user_info.address.state = undefined

        this.syncUsAddressRules(isUs)

        this.$nextTick(() => {
          if (!this.$refs.form) {
            return
          }

          if (isUs) {
            this.$refs.form.validateField('user_info.address.state')
            this.$refs.form.validateField('user_info.address.zipcode')
            return
          }

          this.$refs.form.clearValidate(['user_info.address.state', 'user_info.address.zipcode'])
        })
      }
    },
    'form.payment_info.type': {
      handler() {
        this.form.payment_info.legal_entity = undefined
        this.form.payment_info.method = undefined
        this.form.payment_info.status = undefined
        this.form.payment_info.hourly_rate = 0

        this.fillDefaultRates(this.form.associate_info.role)
      }
    },
    'form.payment_info.method': {
      handler() {
        this.form.payment_info.status = undefined
      }
    },
    is_create_legal_entity: {
      handler(newVal) {
        if (newVal) {
          this.form.payment_info.type = 'contractor'
        }
      },
      immediate: true
    },
    'form.associate_info.role': {
      handler(newVal) {
        this.fillDefaultRates(newVal)
      }
    },
    user_id: {
      handler(newVal) {
        if (newVal) {
          this.rules = {
            ...initialRules,
            user_info: {},
            associate_info: {
              associate_number: [
                {
                  required: true,
                  message: 'Associate number can not be empty',
                  trigger: 'change'
                },
                {
                  len: 6,
                  message: 'The associate number must be 6 characters.',
                  trigger: 'change'
                },
                {
                  validator: this.associateNumberValidator,
                  trigger: 'change'
                }
              ],
              social_security_number: [
                createRequiredTaxIdentifierRule(() => this.form.associate_info.is_ssn)
              ]
            }
          }

          this.syncUsAddressRules(false)
        }
      },
      immediate: true
    }
  },
  computed: {
    agencies() {
      return this.$store.state.applicationState.projectLegalEntities
    },
    visualDateFormat() {
      return this.$store.state.applicationState.dateFormat
    },
    isDisabledPaymentMethod() {
      return !(
        this.form.payment_info.type === 'Contractor' && this.$store.state.applicationState.currentProject.staffing
      )
    },
    roleProxy: {
      get() {
        return this.filters.role_ids ? this.filters.role_ids : undefined
      },
      set(value) {
        this.filters.role_ids = value
      }
    },
    rateProxy: {
      get() {
        return this.form.payment_info.hourly_rate / 100
      },
      set(value) {
        this.form.payment_info.hourly_rate = Math.round(value * 100)
      }
    },
    orientationRateProxy: {
      get() {
        return this.form.payment_info.orientation_rate / 100
      },
      set(value) {
        this.form.payment_info.orientation_rate = Math.round(value * 100)
      }
    },

    trainingRateProxy: {
      get() {
        return this.form.payment_info.training_rate / 100
      },
      set(value) {
        this.form.payment_info.training_rate = Math.round(value * 100)
      }
    },
    salaryProxy: {
      get() {
        return this.form.payment_info.salary / 100
      },
      set(value) {
        this.form.payment_info.salary = Math.round(value * 100)
      }
    },

    isCreatingAssociate() {
      return !!this.user_id
    },
    isVisibleRate() {
      switch (this.form.payment_info.type) {
        case 'employee': {
          return !!this.form.payment_info.method
        }
        case 'volunteer': {
          return false
        }
        case 'contractor': {
          return true
        }
        default:
          return false
      }
    }
  },
  methods: {
    ...profileActions(['createAssociate', 'createUser', 'checkAvailableAssociateNumber']),
    ...legalEntityActions(['getLegalEntity']),
    ...modalActions(['openModal']),

    getTaxIdentifierLabel,

    getUsStateRules(isUs) {
      if (!isUs) {
        return []
      }

      return [
        {
          required: true,
          message: 'Please enter state.',
          trigger: 'change'
        }
      ]
    },

    getZipcodeRules(isUs) {
      if (!isUs) {
        return []
      }

      return [
        {
          required: true,
          message: 'Please enter ZIP code.',
          trigger: 'change'
        }
      ]
    },

    syncUsAddressRules(isUs = isUsCountry(this.form.user_info.address.country)) {
      if (!this.rules.user_info?.address) {
        return
      }

      this.$set(this.rules.user_info.address, 'state', this.getUsStateRules(isUs))
      this.$set(this.rules.user_info.address, 'zipcode', this.getZipcodeRules(isUs))
    },

    revalidateTaxIdentifier(field) {
      this.$nextTick(() => {
        this.$refs.form.validateField(field)
      })
    },

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    fillDefaultRates(id) {
      if (!id) return

      const currentRole = this.roles.find(role => role.id === id)
      if (!currentRole) return

      const fields = ['hourly_rate', 'orientation_rate', 'training_rate']

      const paymentInfo = this.form.payment_info

      fields.forEach(field => {
        if (!paymentInfo[field]) {
          paymentInfo[field] = currentRole[field] ?? 0
        }
      })
    },
    associateNameValidator(rule, value, callback) {
      if (!value) {
        return callback()
      }

      const allowedPattern = /^[a-zA-Z\s\-\/]+$/
      if (!allowedPattern.test(value)) {
        return callback(new Error('Only letters, spaces, hyphens (-), and forward slashes (/) are allowed.'))
      }

      if (value.trim() === '') {
        return callback(new Error('Name cannot be empty.'))
      }

      callback()
    },

    handleNameInput(field, e) {
      const rawValue = e.target.value

      const invalidMatch = rawValue.match(/[^a-zA-Z\s\-\/]/)
      const invalidSymbol = invalidMatch ? invalidMatch[0] : null

      this.invalidChar[field] = invalidSymbol

      const cleaned = rawValue.replace(/[^a-zA-Z\s\-\/]/g, '')

      const formatted = cleaned
        .toLowerCase()
        .split(' ')
        .map(word =>
          word
            .split(/([\-\/])/)
            .map(part => {
              if (part === '-' || part === '/') return part
              return part.charAt(0).toUpperCase() + part.slice(1)
            })
            .join('')
        )
        .join(' ')

      this.form.user_info[field] = formatted

      if (invalidSymbol) {
        setTimeout(() => {
          if (this.invalidChar[field] === invalidSymbol) {
            this.invalidChar[field] = null
          }
        }, 2000)
      }
    },

    getHelpText(field) {
      if (this.invalidChar[field]) {
        const char = this.invalidChar[field]
        const displayChar = char === ' ' ? 'space' : char === '\t' ? 'tab' : `"${char}"`
        return `Invalid character ${displayChar}. ${this.baseHelpText}`
      }
      return this.baseHelpText
    },

    handleOpenLegalEntity() {
      this.openModal({
        type: 'legalEntity',
        data: {
          visible: true,
          initialValues: {
            type: 'independent_contractor',
            address: this.form.user_info.address,
            name: `${this.form.user_info.first_name ?? ''} ${this.form.user_info.last_name ?? ''}`,
            tax_id: (this.form.associate_info.social_security_number ?? '').replace(/\D/g, '')
          },
          add: async legal_entity => {
            this.form.payment_info.legal_entity = legal_entity.id
          },
          showCreateAssociate: false
        }
      })
    },

    closeModal() {
      this.$emit('update:visible', false)
    },

    async associateNumberValidator(rule, value, callback) {
      try {
        const result = await this.checkAvailableAssociateNumber({
          associate_number: value
        })

        if (result.owner) {
          callback(
            new Error(
              `This associate number is already occupied by another user in this company. You can use available associate number: ${result.available_number} `
            )
          )
        }
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      }
    },

    async handleSubmit() {
      this.$refs.form.validate(async valid => {
        if (!valid) return

        try {
          this.loading = true

          this.form.associate_info.is_ssn = this.form.associate_info.is_ssn !== false
          this.form.associate_info.social_security_number =
            normalizeTaxIdentifier(this.form.associate_info.social_security_number) || undefined
          this.form.user_info.email = this.form.user_info.email.toLowerCase()

          const foundedLegalEntity = this.form.payment_info.legal_entity
            ? await this.getLegalEntity({
                legal_entity_id: this.form.payment_info.legal_entity
              })
            : undefined

          const createUserPayload = {
            ...this.form,
            payment_info: {
              ...this.form.payment_info,
              legal_entity: foundedLegalEntity
            }
          }

          const createAssociatePayload = {
            user_id: this.user_id,
            associate_info: this.form.associate_info,
            payment_info: this.form.payment_info,
            creation_mode: this.creation_mode
          }

          let data

          if (this.user_id) {
            data = await this.createAssociate(createAssociatePayload)
          } else {
            data = await this.createUser(createUserPayload)
          }

          this.showNotification('success', 'Info', 'Associate has been created successfully')

          this.closeModal()
          this.$emit('add', data.associate)
        } catch (error) {
          this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
        } finally {
          this.loading = false
        }
      })
    }
  }
}
</script>

<style scoped>
.wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 24px;
}
.ant-row {
  display: flex;
  flex-direction: column;
  margin: 0 !important;
}

.ant-form {
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
}

.ant-input-number {
  width: 100%;
}
</style>
